import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { _HttpClient, SettingsService } from '@delon/theme';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import { UserDefaultService } from 'src/app/shared/services/user-default.service';

import { SupplierAddConfigService } from '../../inline-add-panel/supplier-add-config.service';
import { SelectInstantBaseNewComponent } from '../selectInstant-base-new';

@Component({
  selector: 'app-supplier-select',
  templateUrl: './supplier-select.component.html',
  styles: [
    `
      /* ===== CONTAINER STYLES ===== */
      .supplier-select-container {
        position: relative;
        width: 100%;
      }

      /* ===== ADD NEW BUTTON STYLES ===== */
      ::ng-deep .add-new-button {
        color: #000000 !important;
        font-weight: 500 !important;
        width: 100% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 2px !important;
        background-color: transparent !important;
        border: none !important;
      }

      ::ng-deep .add-new-button .anticon {
        color: #000000 !important;
      }

      /* ===== OCR SUGGESTED ITEMS ===== */
      ::ng-deep .ocr-suggested-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 4px 0;
        cursor: pointer;
        width: 100%;
      }

      ::ng-deep .ocr-text {
        font-weight: 500;
        flex: 1;
      }

      /* ===== ULTRA COMPACT DROPDOWN STYLES ===== */
      ::ng-deep .ant-select-dropdown:has(app-supplier-select-item) {
        max-height: none !important;
        overflow: hidden !important;
        width: 300px !important;
      }

      ::ng-deep
        .ant-select-dropdown:has(app-supplier-select-item)
        .ant-select-dropdown-menu {
        max-height: 240px !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
      }

      ::ng-deep
        .ant-select-dropdown:has(app-supplier-select-item)
        .ant-select-item-option {
        height: auto !important;
        min-height: 60px !important;
        padding: 6px 10px !important;
      }

      /* ===== SUPPLIER ITEM LAYOUT ===== */
      ::ng-deep .ant-select-dropdown app-supplier-select-item {
        display: block !important;
        width: 100% !important;
        height: auto !important;
      }

      ::ng-deep .ant-select-dropdown app-supplier-select-item .width-lg {
        display: block !important;
        width: 100% !important;
      }

      ::ng-deep
        .ant-select-dropdown
        app-supplier-select-item
        .width-lg
        > div:first-child {
        display: flex !important;
        justify-content: space-between !important;
        align-items: center !important;
        width: 100% !important;
      }

      ::ng-deep .ant-select-dropdown app-supplier-select-item nz-tag {
        display: inline-block !important;
        visibility: visible !important;
        opacity: 1 !important;
        margin-left: auto !important;
      }

      /* ===== SPECIAL OPTION HIGHLIGHTING ===== */
      ::ng-deep .ant-select-item-option-content .special-option {
        text-align: center !important;
      }
      //  ::ng-deep .ant-select-dropdown:has(app-supplier-select-item) .ant-select-item-option:has(.section-label)  {
      //         height: auto !important;
      //         min-height: 20px !important;
      //         padding: 6px 10px !important;
      //       }
    `
  ],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SupplierSelectComponent),
      multi: true
    }
  ]
})
export class SupplierSelectComponent
  extends SelectInstantBaseNewComponent
  implements OnInit, AfterViewInit
{
  @ViewChild('selector') nzSelectComponent!: NzSelectComponent;

  @Input() tabindex: string | null = null;

  @Input() activeOnly = true;

  @Input() labelField: 'name' | 'full' | null = null;

  @Input() nzAllowClear: boolean = true;
  @Input() valueToAdd: string = 'supplier';

  @Output() readonly addNewSupplier = new EventEmitter<any>();
  @Output() readonly MoreDetailsClicked = new EventEmitter<any>();

  excludedList: any[] = [];

  NotShowInReport: boolean = false;

  supplierConfig: any;
  inlineDefaults: any = null;

  constructor(
    http: _HttpClient,
    router: Router,
    cdr: ChangeDetectorRef,
    private resueTabService: ReuseTabService,
    private supplierAddConfigService: SupplierAddConfigService,
    private settingsService: SettingsService,
    private uds: UserDefaultService
  ) {
    super(http, cdr, router);
  }

  ngOnInit(): void {
    this.checkAddNewOptionInReportAccess();
    this.supplierConfig = this.supplierAddConfigService.getSupplierConfig();

    // Listen to search form changes to store instant data
    this.searchForm.controls.keyword.valueChanges.subscribe(value => {
      if (value) {
        const trimmedValue = value.trim();
        this.storedInstantData = trimmedValue;
        // Also store in pendingInstantData for Add New (handled by base class)
        this.pendingInstantData = trimmedValue;
      }
    });

    // Ensure the correct item is active whenever the list updates while open
    this.onListFilled.subscribe(() => {
      if ((this.nzSelectComponent as any)?.nzOpen) {
        this.ensureFirstItemActivatedAndFocused();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.tabindex) {
      const nzSelectElement =
        this.nzSelectComponent.originElement.nativeElement;
      const searchInput = nzSelectElement.querySelector('input');

      if (searchInput) {
        searchInput.setAttribute('tabindex', this.tabindex?.toString() || '0');

        // Listen to input events directly
        searchInput.addEventListener('input', (event: any) => {
          if (event.target.value) {
            const trimmedValue = event.target.value.trim();
            this.storedInstantData = trimmedValue;
            // Also store in pendingInstantData for Add New (handled by base class)
            this.pendingInstantData = trimmedValue;
          }
        });
      }
    }
  }

  getLabel(item: any) {
    if (item) {
      if (this.labelField === 'name') return item.name;
      else if (this.labelField === 'full') return `${item.code} - ${item.name}`;
      return item.code;
    }
    return '';
  }

  //!default filter
  // getUrl(searchValue: string): string {
  //   let filter = `status in ('ACTIVE')`;
  //   if (this.activeOnly == false) {
  //     filter = '';
  //   }
  //   let url = `api/suppliers/search?$count=false&$top=50&$skip=0`;
  //   if (filter) url = `${url}&$filter=${filter}`;
  //   if (searchValue && searchValue.trim()) {
  //     url = `${url}&keyword=${encodeURIComponent(searchValue.trim())}`;
  //   } else {
  //     url = `${url}&$orderby=code asc`;
  //   }
  //   return url;
  // }

  //with uds
  getUrl(searchValue: string): string {
    let filter = `status in ('ACTIVE')`;
    if (this.activeOnly == false) {
      filter = '';
    }
    let url = `api/suppliers/search?$count=false&$top=50&$skip=0`;
    if (filter) url = `${url}&$filter=${filter}`;
    if (searchValue && String(searchValue).trim()) {
      url = `${url}&keyword=${encodeURIComponent(String(searchValue).trim())}`;
    } else {
      url = `${url}&$orderby=code asc`;
    }
    return url;
  }

  updateExclusion(excludes: any = []) {
    this.excludedList = excludes;
  }

  // set the location want to navigate
  jump(): string {
    return '/ap/supplier/detail/0';
  }

  // set the param
  queryParam(): String {
    return 'SupplierName';
  }

  checkAddNewOptionInReportAccess(): boolean {
    let curUrl = this.resueTabService.curUrl.toLowerCase();

    if (curUrl.includes('report') || curUrl.includes('einv/')) {
      this.NotShowInReport = true;
    } else {
      this.NotShowInReport = false;
    }

    return this.NotShowInReport;
  }

  // Inline add panel methods
  override addNew(): void {
    // Use pendingInstantData which was captured synchronously in base class onAddNewClick
    // This ensures we have the data even if storedInstantData was cleared
    const instantDataToUse = this.pendingInstantData || this.storedInstantData;

    // If we still don't have data, try to capture it
    if (!instantDataToUse) {
      this.captureCurrentSearchValue();
      const currentSearchValue = this.getCurrentSearchValue();
      if (currentSearchValue) {
        this.pendingInstantData = currentSearchValue;
      }
    }

    // Final fallback - use whatever we have
    const finalInstantData =
      this.pendingInstantData || this.storedInstantData || '';

    // Load options first, then show panel
    this.fetchInlineDefaults().then(
      () => {
        // Merge instant data into initialData as fallback
        // Note: supplier config uses 'name' field for instant data, not 'code'
        if (finalInstantData) {
          this.inlineDefaults = {
            ...this.inlineDefaults,
            name: finalInstantData
          };
        }

        this.showInlineAddPanel = true;
        // Force change detection to ensure the panel is rendered
        this.cdr.detectChanges();

        // Retry mechanism to ensure changeInstantData is called after form is ready
        const trySetInstantData = (retries: number = 10) => {
          if (!finalInstantData) return;

          if (this.inlineAddPanel && this.inlineAddPanel.formGroup) {
            const instantFieldName =
              this.supplierConfig?.fields?.find((f: any) => f.isInstantData)
                ?.name || 'name';
            const control = this.inlineAddPanel.formGroup.get(instantFieldName);
            if (control) {
              this.inlineAddPanel.changeInstantData(finalInstantData);
              return;
            }
          }

          if (retries > 0) {
            setTimeout(() => trySetInstantData(retries - 1), 50);
          }
        };

        // Try immediately and with retries
        setTimeout(() => {
          if (this.inlineAddPanel && finalInstantData) {
            this.inlineAddPanel.changeInstantData(finalInstantData);
          }
          trySetInstantData();
        }, 0);

        if (this.nzSelectComponent) {
          this.nzSelectComponent.setOpenState(false);
        }
      }
    );
  }

  onSupplierCreated(event: { item: any; selectionConfig?: any }): void {
    if (event.item && event.selectionConfig) {
      // Create the supplier object in the expected format
      const supplier = {
        id: event.item[event.selectionConfig.valueField],
        code: event.item.code || event.item.supplierCode,
        name: event.item[event.selectionConfig.labelField],
        controlAccount: event.item.controlAccount,
        currency: event.item.currency,
        term: event.item.term,
        // Add properties that InvoiceService expects
        defaultWVatCodeId: event.item.defaultWVatCodeId || null,
        isGovBirCategory: event.item.isGovBirCategory || false
      };

      // Add the new supplier to the list and select it
      this.list.unshift(supplier);

      // Close the inline panel first
      this.showInlineAddPanel = false;

      // Set the supplier object in the form control to trigger supplier.valueChanges
      this.value = supplier.id;
      this.selected = supplier.id;

      // Set the supplier object directly in the form control
      if (this.valueToAdd === 'supplier') {
        this.onChange(supplier);
        this.writeValue(supplier);
      } else if (this.valueToAdd === 'supplierId') {
        this.onChange(supplier.id);
        this.writeValue(supplier.id);
      }
      // Emit addNewSupplier event for parent component to handle
      this.addNewSupplier.emit(supplier);

      // Clear stored instant data after successful creation
      this.clearStoredInstantData();

      // Force change detection
      this.cdr.detectChanges();
    }
  }

  onSupplierCancelled(): void {
    this.showInlineAddPanel = false;
    // Clear stored instant data when user explicitly cancels
    this.clearStoredInstantData();
  }

  onSupplierMoreDetails(): void {
    // Navigate to supplier details page with all the current form data
    const formData = this.inlineAddPanel?.formGroup?.value;
    if (formData) {
      // Create query params with all form fields
      const queryParams: any = {
        autoPopulate: 1
      };

      // Add all form fields to query params
      if (formData.name) queryParams.SupplierName = formData.name;
      if (formData.controlAccountId)
        queryParams.ControlAccountId = formData.controlAccountId;
      // if (formData.supplierCode)
      //   queryParams.SupplierCode = formData.supplierCode;
      if (formData.currencyId) queryParams.CurrencyId = formData.currencyId;
      if (formData.termId) queryParams.TermId = formData.termId;
      if (formData.purchaserId) queryParams.PurchaserId = formData.purchaserId;
      if (formData.areaId) queryParams.AreaId = formData.areaId;

      queryParams.autoPopulate = 1;

      this.router.navigate(['/ap/supplier/detail/0'], {
        queryParams: queryParams
      });
    } else {
      this.router.navigate(['/ap/supplier/detail/0'], {
        queryParams: {
          autoPopulate: 1
        }
      });
    }
    this.showInlineAddPanel = false;
    this.MoreDetailsClicked.emit();
  }

  onFieldChange(event: { fieldName: string; value: any }): void {
    // Handle field changes - auto-code component will handle code generation
  }

  private loadSelectOptions(): Promise<void> {
    // Load options for control account, currency, term, and purchaser
    return Promise.all([
      this.loadControlAccountOptions(),
      this.loadCurrencyOptions(),
      this.loadTermOptions(),
      this.loadPurchaserOptions()
    ]).then(() => {
      // Force change detection after options are loaded
      this.cdr.detectChanges();

      // Additional change detection for the inline panel
      if (this.inlineAddPanel) {
        // Force change detection on the inline panel
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 100);
      }
    });
  }

  private loadControlAccountOptions(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http
        .get(
          "api/accountcodes/search?$top=50&$skip=0&$filter=IsActive eq true and SpecialCode eq 'CCAC'&$orderby=code asc"
        )
        .subscribe({
          next: (response: any) => {
            const options =
              response.value?.map((item: any) => ({
                label: `${item.code} - ${item.name || item.description}`,
                value: item.id || item.code
              })) || [];

            const controlAccountField = this.supplierConfig.fields.find(
              (f: any) => f.name === 'controlAccountId'
            );
            if (controlAccountField) {
              controlAccountField.options = options;
            }
            resolve();
          },
          error: error => {
            resolve();
          }
        });
    });
  }

  private loadCurrencyOptions(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http
        .get('api/currencies/lookup?$top=50&$skip=0&$orderby=code asc')
        .subscribe({
          next: (response: any) => {
            const options =
              response.value?.map((item: any) => ({
                label: `${item.code}`,
                value: item.id || item.code
              })) || [];

            const currencyField = this.supplierConfig.fields.find(
              (f: any) => f.name === 'currencyId'
            );
            if (currencyField) {
              currencyField.options = options;
            }
            resolve();
          },
          error: error => {
            resolve();
          }
        });
    });
  }

  private loadTermOptions(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http
        .get('api/terms/lookup?$top=50&$skip=0&$orderby=code asc')
        .subscribe({
          next: (response: any) => {
            const options =
              response.value?.map((item: any) => ({
                label: `${item.code} - ${item.name || item.description}`,
                value: item.id || item.code
              })) || [];

            const termField = this.supplierConfig.fields.find(
              (f: any) => f.name === 'termId'
            );
            if (termField) {
              termField.options = options;
            }
            resolve();
          },
          error: error => {
            resolve();
          }
        });
    });
  }

  private loadPurchaserOptions(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http
        .get('api/purchasers/lookup?$top=50&$skip=0&$orderby=code asc')
        .subscribe({
          next: (response: any) => {
            const options =
              response.value?.map((item: any) => ({
                label: `${item.code} - ${item.name || item.description}`,
                value: item.id || item.code
              })) || [];

            const purchaserField = this.supplierConfig.fields.find(
              (f: any) => f.name === 'purchaserId'
            );
            if (purchaserField) {
              purchaserField.options = options;
            }
            resolve();
          },
          error: error => {
            resolve();
          }
        });
    });
  }

  private fetchInlineDefaults(): Promise<void> {
    // Get defaults like detail page when creating a new supplier
    return new Promise(resolve => {
      this.http.get('api/suppliers/new').subscribe({
        next: (data: any) => {
          const defaults: any = {};
          if (data?.controlAccountId)
            defaults.controlAccountId = data.controlAccountId;
          if (data?.currencyId) defaults.currencyId = data.currencyId;
          if (data?.termId) defaults.termId = data.termId;
          if (data?.purchaserId) defaults.purchaserId = data.purchaserId;
          // Map object variants to ids
          if (!defaults.controlAccountId && data?.controlAccount?.id)
            defaults.controlAccountId = data.controlAccount.id;
          if (!defaults.currencyId && data?.currency?.id)
            defaults.currencyId = data.currency.id;
          if (!defaults.termId && data?.term?.id)
            defaults.termId = data.term.id;
          if (!defaults.purchaserId && data?.purchaser?.id)
            defaults.purchaserId = data.purchaser.id;
          this.inlineDefaults = defaults;
          resolve();
        },
        error: () => {
          this.inlineDefaults = null;
          resolve();
        }
      });
    });
  }
}
